import { Injectable } from '@angular/core';
import { FunctionLockService, UtilsService, GlobalsService, HttpService } from '@services';
import { DevTraceService } from '@services/utils/dev-trace.service';
import { IPhotoUploadModel, PhotoUploadModel } from "./photo-upload.model";
import { IDocumentModel } from '@models';
import { Subject } from 'rxjs';
import { SlickToastService } from '@slick-components';

import { Directory, Filesystem } from "@capacitor/filesystem";

const LOCK_KEY = "PHOTOS_STORE_LOCK";
const LOCALSTORAGE_KEY = "PHOTOS_STORE_MODELS";

@Injectable()
export class PhotoUploadStore {
	private static _isLocked: boolean;
	private static readonly UPLOAD_CONCURRENCY = 3;
	private static _errors: string[];
	private uploadStartTimer: ReturnType<typeof setTimeout>;

	photoUploadedSubject: Subject<IDocumentModel> = new Subject<IDocumentModel>();
	photosNeedToUploadSubject: Subject<boolean> = new Subject<boolean>();
	constructor(private functionLockService: FunctionLockService,
		private slickToastService: SlickToastService,
		private httpService: HttpService,
		private devTraceService: DevTraceService) {
		PhotoUploadStore._errors = [];
		PhotoUploadStore._isLocked = true;

		const allPhotos = this.getPhotoUploadModels() || [];

		if (allPhotos.length > 0)
			allPhotos[0].error = null;

		this.setPhotoUploadModels(allPhotos);
		PhotoUploadStore._isLocked = false;
	}

	async uploadPhotos(): Promise<void>{
		if (!GlobalsService.isOnline)
			return;

		if (PhotoUploadStore._isLocked)
			return;

		PhotoUploadStore._isLocked = true;

		try {
			try {
				await Filesystem.readdir({
					path: 'skedit-photos',
					directory: Directory.Data
				});
			}
			catch {
				await Filesystem.mkdir({
					path: 'skedit-photos',
					directory: Directory.Data
				});
			}

			const allPhotos = this.getPhotoUploadModels() || [];
			if (allPhotos.length === 0) {
				this.photosNeedToUploadSubject.next(false);
				return;
			}
			this.devTraceService.addTrace(`${allPhotos.length} photos to upload`);

			allPhotos.forEach(photo => {photo.error = null;photo.currentStatus = 'Waiting';});

			this.setPhotoUploadModels(allPhotos);

			for (let index = 0; index < allPhotos.length; index += PhotoUploadStore.UPLOAD_CONCURRENCY)
			{
				if (!GlobalsService.isOnline) {
					this.devTraceService.addTrace('Device is offline');
					break;
				}

				const batch = allPhotos.slice(index, index + PhotoUploadStore.UPLOAD_CONCURRENCY);

				await Promise.all(batch.map(photo => this.upload_photo_internal(photo)));
			}

			const remainingPhotos = this.getPhotoUploadModels() || [];



			if (remainingPhotos.length === 0) {
				this.photosNeedToUploadSubject.next(false);
				this.slickToastService.showSuccess('All photos uploaded', 2000);
			}
			else {
				this.photosNeedToUploadSubject.next(true);

				this.devTraceService.addTrace(`${remainingPhotos.length} photos still waiting to upload`);
			}
		}
		catch (error) {
			const errMsg = this.getErrorMessage(error);

			console.error('Photo upload queue failed:', error);

			this.devTraceService.addTrace(`Photo upload queue failed: ${errMsg}`);
		}
		finally {

			PhotoUploadStore._isLocked = false;
		}
	}

	private async upload_photo_internal(photo: IPhotoUploadModel): Promise<IPhotoUploadModel> {
		try {
			// Upload photo
			photo.currentStatus = 'Uploading';
			photo.error = null;
			await this.updatePhoto(photo);

			const imageFile = await Filesystem.readFile({
				path: `skedit-photos/${photo.fileName}`,
				directory: Directory.Data
			});

			const uploadUrl = `documents/saveJobPhoto2?folderUuid=${photo.folderUuid}&uuid=${photo.uuid}`;

			const formData = new FormData();
			formData.append('enc64', imageFile.data as string);

			this.devTraceService.addTrace(`Uploading file: ${uploadUrl}`);
			const result = await this.httpService.postMultipart(uploadUrl, formData);

			if (result?.status === 'ok') {
				this.devTraceService.addTrace(`Uploading file: ${uploadUrl} succeeded`);
				const doc = await this.httpService.get(`documents/getDocumentByUuid?uuid=${photo.uuid}`);
				this.photoUploadedSubject.next(doc);

				// Delete this photo
				await this.clearPhoto(photo);
			}
			else {
				photo.retryCount = (photo.retryCount || 0) + 1;
				photo.currentStatus = 'Error';
				photo.error = `Upload failed: ${result?.status ?? 'Unknown status'}`;
				await this.updatePhoto(photo);
				this.devTraceService.addTrace(`Uploading file: ${uploadUrl} failed: ${result?.status ?? 'Unknown status'}`);
			}
		}
		catch (error) {
			const errMsg = this.getErrorMessage(error);
			photo.error = errMsg;
			photo.currentStatus = 'Error';
			photo.retryCount = (photo.retryCount || 0) + 1;

			// Keep the file and queue entry so it can retry later.
			await this.updatePhoto(photo);
			console.error('Photo upload failed:', error);
			if (PhotoUploadStore._errors.indexOf(errMsg) < 0)
				PhotoUploadStore._errors.push(errMsg);
			this.devTraceService.addTrace(`Photo upload error: ${errMsg}`);
		}

		return photo;
	}

	async addPhoto(fileName: string, folderUuid: string): Promise<IPhotoUploadModel> {
		this.photosNeedToUploadSubject.next(true);
		const newPhotoUploadModel = new PhotoUploadModel();

		const photosUploadModels = this.getPhotoUploadModels() || [];

		newPhotoUploadModel.uuid = UtilsService.newGuid();
		newPhotoUploadModel.date = new Date();
		newPhotoUploadModel.folderUuid = folderUuid;
		newPhotoUploadModel.fileName = fileName;
		newPhotoUploadModel.retryCount = 0;
		newPhotoUploadModel.currentStatus = 'Waiting';

		photosUploadModels.push(newPhotoUploadModel);
		this.setPhotoUploadModels(photosUploadModels);

		// Let all photos from a multi-select enter the queue before starting.
		clearTimeout(this.uploadStartTimer);

		this.uploadStartTimer = setTimeout(() => {void this.uploadPhotos();}, 250);

		return newPhotoUploadModel;
	}

	async clearAll(): Promise<IPhotoUploadModel[]> {

		this.setPhotoUploadModels([]);

		this.photosNeedToUploadSubject.next(false);

		return this.getAllPhotos();

	}

	async getAllPhotos(): Promise<IPhotoUploadModel[]> {
		return [...this.getPhotoUploadModels()];
	}

	async clearPhoto(photo: IPhotoUploadModel): Promise<void> {

		let photoUploadModels = this.getPhotoUploadModels();

		photoUploadModels = photoUploadModels.filter(x => x.uuid !== photo.uuid);

		this.setPhotoUploadModels(photoUploadModels);

		try {
			await Filesystem.deleteFile({
				path: `skedit-photos/${photo.fileName}`,
				directory: Directory.Data
			});
		}
		catch (error) {
			console.error(error);
		}
	}

	async updatePhoto(photo: IPhotoUploadModel): Promise<IPhotoUploadModel> {

		const photoUploadModels = this.getPhotoUploadModels();

		const photoIdx = photoUploadModels.findIndex(x => x.uuid === photo.uuid);

		if (photoIdx >= 0)
			photoUploadModels[photoIdx] = photo;

		this.setPhotoUploadModels(photoUploadModels);

		return photoUploadModels[photoIdx];
	}

	private getPhotoUploadModels(): IPhotoUploadModel[] {
		const photoModelStr = localStorage.getItem(LOCALSTORAGE_KEY);

		if (!photoModelStr)
			return [];

		return JSON.parse(photoModelStr);
	}

	private setPhotoUploadModels(photoModels: IPhotoUploadModel[]): void
	{
		const photoModelStr = JSON.stringify(photoModels);

		localStorage.setItem(LOCALSTORAGE_KEY, photoModelStr);
	}

	private getErrorMessage(error: any): string {
		if (error instanceof Error)
			return error.message;

		if (typeof error === 'string')
			return error;

		try {
			return JSON.stringify(error) || 'Unknown error';
		}
		catch {
			return 'Unknown error';
		}
	}
}
